import React, { useState } from "react";
import { toast } from "react-toastify";

import Button from "../../../components/form/Button";
import Table from "../../../components/elements/table/Table";

import useForm from "../../../hooks/useForm";
import Input from "../../../components/form/Input";
import Search from "../../../components/form/Search";
import { setTitle } from "../../../helpers/MetaTag";
import { handleFilterChange } from "../../../helpers";
import DeleteModal from "../../../components/common/DeleteModal";
import useFetch from "../../../hooks/useFetch";
import { useDebounce } from "../../../hooks/useDebounce";
import LimitSelect from "../../../components/elements/LimitSelect";
import useMutation from "../../../hooks/useMutation";

import Switch from "../../../components/form/Switch";
import Badge from "../../../components/elements/Badge";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  taskDelete,
  taskList,
  taskStatusChange,
} from "../../../services/taskService";
import { userList as getUserList } from "../../../services/userService";
import ManageTaskModal from "./modals/ManageTaskModal";
import DatePicker from "../../../components/form/DatePicker";
const Task = () => {
  setTitle("Plotics Admin | Task");
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  //userId and name filter states ==================>
  const [userKeyword, setUserKeyword] = useState("");
  const userSearch = useDebounce(userKeyword, 300);

  const [userFilters] = useState({
    limit: 500,
    page: 1,
    sortQuery: "-createdAt",
  });

  const {
    data: userList,
    error: userError,
    isLoading: userLoading,
  } = useFetch({
    dependencies: ["userList", userFilters, userSearch],
    fetchFunction: async () => {
      const res = await getUserList({
        ...userFilters,
        ...(userSearch && { keyword: userSearch }),
      });

      return res?.docs?.data || [];
    },
  });

  const { data, error, isLoading, updateData, isFetching, refetch } = useFetch({
    dependencies: ["task", filters, search, dateRange],
    fetchFunction: async () => {
      const {
        taskStatus = [],
        status = [],
        userId = [],
        isFeatured = [],
        ...restFilters
      } = filters || {};
      const res = await taskList({
        ...restFilters,
        ...Object.fromEntries(
          taskStatus.map((val, i) => [`taskStatus[${i}]`, val])
        ),
        ...Object.fromEntries(status.map((val, i) => [`status[${i}]`, val])),
        ...Object.fromEntries(userId.map((val, i) => [`userId[${i}]`, val])),
        ...Object.fromEntries(
          isFeatured.map((val, i) => [`isFeatured[${i}]`, val])
        ),
        ...(search && { keyword: search }),
        ...(dateRange?.startDate && {
          startDate: dayjs(dateRange.startDate).toISOString(),
        }),
        ...(dateRange?.endDate && {
          endDate: dayjs(dateRange.endDate).toISOString(),
        }),
      });
      return res?.docs;
    },
  });

  //   task status and featured change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status, featured }) => {
        const payload = {
          ids: id,
          ...(typeof status !== "undefined" && { status }),
          ...(typeof featured !== "undefined" && { featured }),
        };

        const res = await taskStatusChange(payload);
        return { res, payload };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status !== 200) {
          updateData((pre) => {
            pre.data = (pre?.data || []).map((elm) => {
              if (elm?._id === payload?.ids) {
                return {
                  ...elm,
                  ...(payload.status && {
                    status: payload.status === 1 ? 2 : 1,
                  }),
                  ...(payload.featured && {
                    featured: payload.featured === 1 ? 2 : 1,
                  }),
                };
              }
              return elm;
            });
            return { ...pre };
          });
        }
      },
      onError: (error) => {
        console.error(" Tasks Status change error:", error);
      },
    });

  const tableHeadData = [
    {
      _id: 1,
      name: " Title",
      align: "left",
    },
    // {
    //   _id: 2,
    //   name: "Description",
    //   align: "left",
    // },

    {
      _id: "userId",
      name: "user",
      align: "left",
      isFilter: true,
      isFilterSearch: true,
      filterSearch: (e) => setUserKeyword(e),
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            userId: val.map((item) => item.value),
          }));
        } else {
          setFilters((pre) => {
            delete pre.userId;
            return { ...pre };
          });
        }
      },
      filterData: (userList || []).map((user) => ({
        value: user._id,
        name: `${user.firstName} ${user.lastName}`,
        type: "checkbox",
      })),
    },

    {
      _id: "featured",
      name: "Featured",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            isFeatured: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.isFeatured;
            return { ...pre };
          });
        }
      },
      filterData: [
        { name: "Featured", value: 1, type: "checkbox" },
        { name: "Unfeatured", value: 2, type: "checkbox" },
      ],
    },
    {
      _id: "date",
      name: "Published date",
      align: "left",
    },
    // {
    //   _id: 3,
    //   name: "Favourites",
    //   align: "left",
    // },
    {
      _id: "taskStatus",
      name: "Task Status",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            taskStatus: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.taskStatus;
            return { ...pre };
          });
        }
      },
      filterData: [
        { name: "Pending", value: 1, type: "checkbox" },
        { name: "Inprogress", value: 2, type: "checkbox" },
        { name: "Completed", value: 3, type: "checkbox" },
      ],
    },

    {
      _id: "status",
      name: "Status",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            status: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.status;
            return { ...pre };
          });
        }
      },
      filterData: [
        { name: "Published", value: 1, type: "checkbox" },
        { name: "Unpublished", value: 2, type: "checkbox" },
      ],
    },
    {
      _id: 5,
      name: "Action",
      align: "left",
      isFilter: false,
      isSort: false,
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: item?._id,
          type: "user",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.title || "N/A",
          image: item?.media?.[0]?.url,
          isTooltip: false,
        },
        {
          _id: "user",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            `${item?.userData?.firstName} ${item?.userData?.lastName}` || "-",
        },
        // {
        //   _id: 2,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data:
        //     (
        //       <div
        //         className="line-clamp-2"
        //         dangerouslySetInnerHTML={{ __html: item?.content || "-" }}
        //       ></div>
        //     ) || "N/A",
        // },
        // {
        //   _id: 3,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data: (
        //     <div className="flex items-center">
        //       <div
        //         className={`text-[11px] font-normal leading-tight rounded py-1 px-2 border capitalize ${
        //           item?.type === "post"
        //             ? "bg-blue-100 text-blue-600/80 border-blue-200/50"
        //             : "bg-green-100 text-green-600/80 border-green-200/50"
        //         }`}
        //       >
        //         {item?.type}
        //       </div>
        //     </div>
        //   ),
        // },
        {
          _id: "featured",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              <Switch
                className={
                  "!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"
                }
                checked={item?.featured === 1}
                onChange={() => {
                  updateData((pre) => {
                    pre.data = (pre?.data || []).map((elm) =>
                      elm?._id === item?._id
                        ? { ...elm, featured: elm?.featured === 1 ? 2 : 1 }
                        : elm
                    );
                    return { ...pre };
                  });
                  statusChangeHandler({
                    id: item?._id,
                    featured: item?.featured === 1 ? 2 : 1,
                  });
                }}
              />
              <Badge
                type={item?.featured === 1 ? "success" : "danger"}
                label={item?.featured === 1 ? "Featured" : "UnFeatured"}
              />
            </div>
          ),
        },
        {
          _id: "date",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: dayjs(item?.publishedAt).format("DD MMM, YYYY") || "-",
        },
        // {
        //   _id: 3,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data: item.favourite?.length || 0,
        // },
        // {
        //   _id: 4,
        //   type: "status",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   statusLabel:
        //     item?.status === 1
        //       ? "Published"
        //       : item?.status === 2
        //       ? "UnPublished"
        //       : "Draft",
        //   statusType: item?.status === 1 ? "success" : "danger",
        // },
        {
          _id: "taskStatus",
          type: "status",
          align: "left",
          cellClass: "!min-w-[100px]",
          statusLabel:
            item?.taskStatus === 1
              ? "Pending"
              : item?.taskStatus === 2
              ? "Inprogress"
              : "Completed",
          statusType: item?.taskStatus === 1 ? "danger" : "success",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              <Switch
                className={
                  "!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"
                }
                checked={item?.status === 1}
                onChange={() => {
                  updateData((pre) => {
                    pre.data = (pre?.data || []).map((elm) =>
                      elm?._id === item?._id
                        ? { ...elm, status: elm?.status === 1 ? 2 : 1 }
                        : elm
                    );
                    return { ...pre };
                  });
                  statusChangeHandler({
                    id: item?._id,
                    status: item?.status === 1 ? 2 : 1,
                  });
                }}
              />
              <Badge
                type={item?.status === 1 ? "success" : "danger"}
                label={item?.status === 1 ? "Published" : "Unpublished"}
              />
            </div>
          ),
        },
        {
          _id: 5,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 1,
              name: "Edit",
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
              standout: false,
            },
            {
              _id: 3,
              name: "Details",
              icon: "fa-regular fa-eye",
              standout: false,
              onClick: () => {
                navigate(`/admin/task/details/${item._id}`);
              },
            },

            {
              _id: 2,
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({ isOpen: true, data: item });
              },
            },
          ],
        },
      ],
    };
  });

  // add or edit task =================>
  function taskManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          updateData((pre) => ({ ...pre, data: [res?.data, ...pre?.data] }));
          refetch();
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        updateData((pre) => ({
          ...pre,
          data: pre?.data?.map((item) =>
            item?._id === res?.data?._id ? res?.data : item
          ),
        }));
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message);
    }
  }

  //   task delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await taskDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        updateData((pre) => {
          pre.data = (pre?.data || [])?.filter(
            (item) => !payload?.includes(item?._id)
          );
          return { ...pre };
        });
        if (data?.metadata?.totalDocs > filters?.limit) {
          refetch();
        }
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        <div className="font-bold text-center text-lg xl:text-3xl text-chestnut-rose">
          Tasks
        </div>
        <div className="flex items-center gap-3">
          <div className="w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={(val) => handleFilterChange("limit", val, setFilters)}
            />
          </div>
          <div className="w-72">
            <Search
              placeholder={"Search here"}
              search={(val) => {
                setKeyword(val);
                handleFilterChange("page", 0, setFilters);
              }}
            />
          </div>
          <div className="w-72">
            <DatePicker
              // label="Date Range"
              value={dateRange}
              renge={true}
              onChange={setDateRange}
              placeholder="Select date range"
              // className="!w-36"
            />
          </div>
          <div className="relative">
            <Button
              buttonClasses={
                "!bg-transparent border border-transparent !h-10 hover:!bg-chestnut-rose/10 hover:!border-chestnut-rose/10"
              }
              buttonLabel={"Create New"}
              buttonLabelClasses={"font-medium !text-sm text-chestnut-rose"}
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
          </div>
        </div>
      </div>
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
          setSelectedItems([]);
        }}
        handleOrdering={(val) => {
          console.log(val);
        }}
        isFetching={isFetching}
        isOrderingDisabled={true}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
      />
      <ManageTaskModal
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={taskManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Task;
